/**
 * Helper: Format Boost
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 *
 */
import { formatMoney } from '@shopify/theme-currency'

import { formatPrice } from '~/helpers/format'
import { decodeApiId } from '~/helpers/graphql'

/**
 * Formats product from Boost API call.
 * @param {Object} product
 * @returns {Object}
 */
export function formatBoostProduct(product) {
  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice({
      maxVariantPrice: {
        amount: product.compare_at_price_max,
      },
      minVariantPrice: {
        amount: product.compare_at_price_min,
      },
    }),
    description: product.body_html,
    handle: product.handle,
    id: product.id,
    images: formatBoostImages(product),
    options: formatBoostOptions(product),
    price: formatPrice({
      maxVariantPrice: {
        amount: product.price_max,
      },
      minVariantPrice: {
        amount: product.price_min,
      },
    }, true),
    tags: product.tags,
    title: product.title,
    type: product.product_type,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatBoostVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  /**
   * Product badges.
   */
  formattedProduct.badges = product.tags.map((tag) => {
    if (!tag.includes('Badge:')) {
      return false
    }

    return tag.split('Badge:')[1].trim()
  })

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
    coming_soon: false,
    custom_id: '',
    vendor_link: '',
    product_title: product.title,
  }

  Object.keys(metafields).forEach((key) => {
    const object = product.metafields?.find((metafield) => {
      return metafield?.key === key
    })

    if (!object) {
      return
    }

    let value = object.value
    let formattedKey = key

    switch (key) {
      case 'colours':
        value = JSON.parse(object.value)
        break

      case 'coming_soon':
        formattedKey = 'isComingSoon'
        value = JSON.parse(Boolean(object.value))
        delete metafields[key]
        break

      case 'custom_id':
        formattedKey = 'customId'
        delete metafields[key]
        break

      case 'product_title':
        value = object.value
        break

      case 'vendor_link':
        value = value.replace(
          'https://childsplayredirect.com/',
          `//${location.host}${cnvs.routes.normalisedRoot}`,
        )

        formattedKey = 'vendorLink'
        delete metafields[key]
        break
    }

    metafields[formattedKey] = value
  })

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Formats boost images
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostImages(product) {
  return product.images_info.map((image) => {
    return {
      alt: image.alt,
      height: image.height,
      id: image.id,
      src: image.src,
      width: image.width,
    }
  })
}

/**
 * Formats boost options
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostOptions(product) {
  return product.options_with_values.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values.map((value) => value.title),
    }
  })
}

/**
 * Format boost variants
 * @param {Object} product
 * @param {Boolean} isGiftCard
 * @returns {Array}
 */
function formatBoostVariants(product, isGiftCard) {
  return product.variants.map((variant) => {
    const image = product.images_info.find((imageInfo) => {
      return imageInfo.src === variant.image
    })

    const formattedVariant = {
      available: variant.available,
      id: variant.id,
      image: image ? image.id : null,
      inventory: variant.inventory_quantity,
      price: {
        amount: Number(variant.price) * 100,
      },
      selectedOptions: variant.merged_options.map((option) => {
        const data = option.split(':')
        return {
          name: data[0],
          value: data[1],
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat).replaceAll(',', '')
      : variant.title

    return formattedVariant
  })
}

/**
 * Format Shopify Market prices.
 * @param {Object} response - GraphQL response.
 */
export function formatLocalisedPriceData(response) {
  const product = response.node

  return {
    id: decodeApiId(product.id),
    price: formatPrice(product.priceRange),
    compareAtPrice: formatPrice(product.compareAtPriceRange),
  }
}

/**
 * Format collection from Boost API call.
 * @param {Object} collection
 * @returns {Object}
 */
export function formatBoostCollection(collection) {
  return {
    description: collection.body_html,
    handle: collection.handle,
    id: collection.id,
    pageInfo: {},
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }
}

/**
 * Format page from Boost API call.
 * @param {Object} page
 * @returns {Object}
 */
export function formatBoostPage(page) {
  return {
    content: page.body_html,
    handle: page.handle,
    id: page.id,
    title: page.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}pages/${page.handle}`,
  }
}
